import { clsx } from 'clsx';

// Model configuration with colors and identifiers
export const MODEL_CONFIG: Record<string, {
  color: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  initial: string;
  name: string;
  provider: string;
}> = {
  CLAUDE_OPUS_4_5: {
    color: '#7c3aed',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
    initial: 'C',
    name: 'Claude Opus 4.5',
    provider: 'Anthropic',
  },
  CLAUDE_OPUS_4_6: {
    color: '#6d28d9',
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-700',
    borderColor: 'border-violet-200',
    initial: 'C6',
    name: 'Claude Opus 4.6',
    provider: 'Anthropic',
  },
  GPT_5_2_PRO: {
    color: '#16a34a',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
    initial: 'G',
    name: 'GPT-5.2 Pro',
    provider: 'OpenAI',
  },
  GEMINI_3_PRO: {
    color: '#2563eb',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    initial: 'Ge',
    name: 'Gemini 3 Pro',
    provider: 'Google',
  },
  GRAND_ROUNDS: {
    color: '#d97706',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    initial: 'GR',
    name: 'Grand Rounds',
    provider: 'Consensus',
  },
};

interface ModelLogoProps {
  model: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  showName?: boolean;
}

export function ModelLogo({ model, size = 'md', className, showName = false }: ModelLogoProps) {
  const config = MODEL_CONFIG[model] || {
    color: '#6b7280',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
    initial: model.charAt(0),
    name: model,
    provider: 'Unknown',
  };

  const sizeClasses = {
    xs: 'w-5 h-5 text-[10px]',
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
  };

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <div
        className={clsx(
          'rounded-lg flex items-center justify-center font-bold text-white shrink-0',
          sizeClasses[size]
        )}
        style={{ backgroundColor: config.color }}
        title={config.name}
      >
        {config.initial}
      </div>
      {showName && (
        <div className="min-w-0">
          <p className={clsx('font-medium truncate', config.textColor)}>{config.name}</p>
          <p className="text-xs text-gray-400 truncate">{config.provider}</p>
        </div>
      )}
    </div>
  );
}

// Model badge for inline display
interface ModelBadgeProps {
  model: string;
  size?: 'sm' | 'md';
  className?: string;
  showLogo?: boolean;
}

export function ModelBadge({ model, size = 'md', className, showLogo = true }: ModelBadgeProps) {
  const config = MODEL_CONFIG[model] || {
    color: '#6b7280',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
    initial: model.charAt(0),
    name: model,
    provider: 'Unknown',
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-sm gap-1.5',
  };

  const logoSizes = {
    sm: 'w-3.5 h-3.5 text-[8px]',
    md: 'w-4 h-4 text-[10px]',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full font-medium border',
        config.bgColor,
        config.textColor,
        config.borderColor,
        sizeClasses[size],
        className
      )}
    >
      {showLogo && (
        <span
          className={clsx(
            'rounded flex items-center justify-center font-bold text-white shrink-0',
            logoSizes[size]
          )}
          style={{ backgroundColor: config.color }}
        >
          {config.initial}
        </span>
      )}
      {config.name}
    </span>
  );
}

// Model color utility
export function getModelColor(model: string): string {
  return MODEL_CONFIG[model]?.color || '#6b7280';
}

export function getModelName(model: string): string {
  return MODEL_CONFIG[model]?.name || model;
}
